import { useCallback, useState } from 'react';
import {
    Alert,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { PageHeader } from '../components/PageHeader';
import { JobForm } from '../components/jobs/JobForm';
import { NewJobDialog } from '../components/jobs/NewJobDialog';
import { useJobDetail, useJobList, useJobReference } from '../hooks/useJobs';

/**
 * Jobs.
 *
 * 293 of the 392 production jobs are Inactive, so Active is the default filter
 * — but it is a visible toggle, not a hidden one.
 *
 * The list view follows click2gro's General Tasks page: full-width container,
 * filter bar in a card, standard-height striped rows, client-side pagination.
 * The API returns every job for a status (a few hundred at most), so paging in
 * the browser costs nothing and keeps search instant.
 */

const STATUS_FILTERS = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'all', label: 'All' },
] as const;

/** YYYY-MM-DD to DD-MM-YYYY, as the job's Profit & Loss tab shows dates. */
function formatDate(iso: string | null): string {
    if (iso === null || iso === '') {
        return '—';
    }
    const d = iso.slice(0, 10).split('-');
    return d.length === 3 ? `${d[2] ?? ''}-${d[1] ?? ''}-${d[0] ?? ''}` : iso;
}

export function Jobs(): React.JSX.Element {
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('Active');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selected, setSelected] = useState<number | null>(null);
    const [creating, setCreating] = useState(false);
    // Bumped on each open so the dialog starts from a blank form.
    const [createKey, setCreateKey] = useState(0);
    const { jobs, loading, error, reload } = useJobList(search, status);
    const { detail, reload: reloadDetail } = useJobDetail(selected);
    const { tasks, clients } = useJobReference();

    const handleSaved = useCallback(() => {
        reloadDetail();
        reload();
    }, [reloadDetail, reload]);

    const handleDeleted = useCallback(() => {
        setSelected(null);
        reload();
    }, [reload]);

    // Clamp rather than reset in an effect: a search that shrinks the list
    // should land on its last page, not an empty one.
    const lastPage = Math.max(0, Math.ceil(jobs.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = jobs.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="Jobs" subtitle="View and manage all jobs" />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        flexWrap="wrap"
                        sx={{ flex: 1 }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search number, address or suburb..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            slotProps={{
                                input: { startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> },
                            }}
                            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 360 } }}
                        />
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                            {STATUS_FILTERS.map((f) => (
                                <Button
                                    key={f.value}
                                    size="small"
                                    variant={status === f.value ? 'contained' : 'outlined'}
                                    color={status === f.value ? 'primary' : 'inherit'}
                                    onClick={() => {
                                        setStatus(f.value);
                                        setPage(0);
                                    }}
                                    sx={{ flex: { xs: '1 1 30%', sm: '0 0 auto' } }}
                                >
                                    {f.label}
                                    {status === f.value && !loading ? ` (${String(jobs.length)})` : ''}
                                </Button>
                            ))}
                            {loading ? <CircularProgress size={18} /> : null}
                        </Stack>
                    </Stack>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setCreateKey((k) => k + 1);
                            setCreating(true);
                        }}
                    >
                        New Job
                    </Button>
                </Stack>
            </Paper>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
                    {error}
                </Alert>
            ) : null}

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Address</TableCell>
                            <TableCell>Suburb</TableCell>
                            <TableCell>Client</TableCell>
                            <TableCell>Registered Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary">
                                        {loading
                                            ? 'Loading jobs...'
                                            : search.trim() === ''
                                              ? 'No jobs here.'
                                              : `No jobs match "${search}".`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageRows.map((j, idx) => (
                                <TableRow
                                    key={j.id}
                                    hover
                                    onClick={() => {
                                        setSelected(j.id);
                                    }}
                                    sx={{ cursor: 'pointer', bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}
                                >
                                    <TableCell sx={{ fontWeight: 600 }}>{j.jobAddress}</TableCell>
                                    <TableCell>{j.jobSuburb ?? '—'}</TableCell>
                                    <TableCell>{j.clientName ?? '—'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(j.jobRegisterDate)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={j.status}
                                            color={j.status === 'Active' ? 'success' : 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelected(j.id);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={jobs.length}
                page={currentPage}
                onPageChange={(_e, p) => {
                    setPage(p);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(Number.parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 1 }}
            />

            <Dialog
                // Opens once the job has loaded, so there is no empty dialog or spinner in between.
                open={selected !== null && detail !== null && detail.job.id === selected}
                onClose={() => {
                    setSelected(null);
                }}
                maxWidth="lg"
                fullWidth
                slotProps={{ paper: { elevation: 1 } }}
            >
                {detail !== null && detail.job.id === selected ? (
                    <JobForm
                        detail={detail}
                        tasks={tasks}
                        clients={clients}
                        onSaved={handleSaved}
                        onDeleted={handleDeleted}
                        onClose={() => {
                            setSelected(null);
                        }}
                    />
                ) : null}
            </Dialog>

            <NewJobDialog
                key={createKey}
                open={creating}
                clients={clients}
                onCancel={() => {
                    setCreating(false);
                }}
                onCreated={(j) => {
                    setCreating(false);
                    reload();
                    setSelected(j.id);
                }}
            />
        </Container>
    );
}
